import { useDownloadDocumentCategory } from "@/modules/property-tracks/hooks/download-document-category.hook";
import useDownloadDocument from "@/modules/property-tracks/hooks/download-document.hook";
import {
  TRACK_DOCUMENT_TYPES,
  TrackDocument,
  TrackDocumentType,
} from "@/modules/property-tracks/types";
import {
  Button,
  IconButton,
  Typography,
  useTheme,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import { format } from "date-fns";
import { ArrowDownToLine, MoreVertical } from "lucide-react";
import { Fragment } from "react/jsx-runtime";
import UploadDocument from "./upload-document";
import useDeleteDocument from "@/modules/property-tracks/hooks/delete-document.hooks";
import { useState } from "react";
import ConfirmationModal from "@/modules/common/components/modals/cofirmation-modal";
import { markDocumentAsChosenByClient } from "@/modules/property-tracks/api";
import { useQueryClient } from "@tanstack/react-query";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";

const DocumentKey = ({ document }: { document: TrackDocument }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { download, isDownloading } = useDownloadDocument();
  const { deleteDocument, isDeleting } = useDeleteDocument();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = () => {
    download(document.uid);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    deleteDocument(document.uid);
    setShowDeleteModal(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleChooseByClientClick = async () => {
    const res = await markDocumentAsChosenByClient(
      document.uid,
      !document.chosenByClient
    );
    if (res.status === 204) {
      queryClient.invalidateQueries({ queryKey: ["track-documents"] });
      showToast({
        type: ToastType.SUCCESS,
        message: "Document marked as chosen by client",
        toastId: TOAST_IDS.MARK_DOCUMENT_AS_CHOSEN_BY_CLIENT_SUCCESS,
      });
    } else {
      showToast({
        type: ToastType.ERROR,
        message: "Failed to mark document as chosen by client",
        toastId: TOAST_IDS.MARK_DOCUMENT_AS_CHOSEN_BY_CLIENT_ERROR,
      });
    }
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack>
        <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
          {document.fileName}
        </Typography>

        <Typography
          variant="caption"
          color={theme.nawy.colors.semantic.text.secondary}
          sx={{
            ...theme.nawy.typography.english.variants.body.xs.regular,
          }}
        >
          {format(new Date(document.createdAt), "dd MMMM yyyy")}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center">
        {document.documentType === "Layouts" && (
          <Stack
            bgcolor={
              document.chosenByClient
                ? theme.nawy.colors.primitives.green[500]
                : theme.nawy.colors.primitives.yellow[600]
            }
            borderRadius={(theme) => theme.nawy.radius["2xl"]}
            p={(theme) => theme.nawy.spacing.xs}
            width="fit-content"
            sx={{
              cursor: document.chosenByClient ? "default" : "pointer",
              userSelect: "none",
            }}
            onClick={() =>
              !document.chosenByClient && handleChooseByClientClick()
            }
          >
            <Typography
              fontSize={10}
              fontWeight={600}
              color={theme.nawy.colors.primitives.white[100]}
            >
              {document.chosenByClient
                ? "Chosen by client"
                : "Mark as chosen by client"}
            </Typography>
          </Stack>
        )}

        <IconButton
          onClick={handleMenuOpen}
          disabled={isDownloading || isDeleting}
        >
          <MoreVertical
            size={16}
            color={theme.nawy.colors.primitives.blue[500]}
          />
        </IconButton>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: theme.nawy.radius.lg,
              minWidth: 140,
            },
          },
        }}
      >
        <MenuItem onClick={handleDownload}>Download</MenuItem>
        <MenuItem
          onClick={handleDeleteClick}
          sx={{ color: theme.palette.error.main }}
        >
          Delete
        </MenuItem>
      </Menu>

      <ConfirmationModal
        open={showDeleteModal}
        setOpen={setShowDeleteModal}
        onConfirmHandler={handleDeleteConfirm}
        onCancelHandler={handleDeleteCancel}
        title="Delete Document"
        body="Are you sure you want to delete this document? This action cannot be undone."
        isLoading={isDeleting}
      />
    </Stack>
  );
};

interface DocumentCategoryCardProps {
  documents: TrackDocument[];
  documentType: TrackDocumentType;
}

const DocumentCategoryCard = ({
  documentType,
  documents,
}: DocumentCategoryCardProps) => {
  const theme = useTheme();

  const { isFetching, refetch } = useDownloadDocumentCategory(documentType);

  return (
    <Stack
      bgcolor={theme.nawy.colors.primitives.white[100]}
      gap={theme.nawy.spacing.sm}
      borderRadius={theme.nawy.radius.lg}
      padding={theme.nawy.spacing.md}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          sx={{ ...theme.nawy.typography.english.variants.body.md.semibold }}
        >
          {TRACK_DOCUMENT_TYPES[documentType]}
        </Typography>

        {documents.length > 0 && <UploadDocument documentType={documentType} />}
      </Stack>

      {documents.length ? (
        <Fragment>
          <Stack>
            {documents.map((doc: TrackDocument) => {
              return <DocumentKey key={doc.uid} document={doc} />;
            })}
          </Stack>

          <Stack direction="row" alignItems="center">
            <Button
              onClick={() => refetch()}
              disabled={isFetching}
              variant="contained"
              startIcon={<ArrowDownToLine size={16} />}
              fullWidth
              sx={{
                backgroundColor: theme.nawy.colors.primitives.navy[100],
                color: theme.nawy.colors.primitives.blue[500],
                textTransform: "none",
              }}
            >
              Download All
            </Button>
          </Stack>
        </Fragment>
      ) : (
        <>
          <Typography
            variant="caption"
            color={theme.nawy.colors.semantic.text.secondary}
            sx={{
              ...theme.nawy.typography.english.variants.body.md.regular,
            }}
          >
            The property has no{" "}
            {TRACK_DOCUMENT_TYPES[documentType].toLowerCase()} yet.
          </Typography>
          <UploadDocument documentType={documentType} showLabel />
        </>
      )}
    </Stack>
  );
};

export default DocumentCategoryCard;
